import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const pricingData = [{
  name: "STARTER",
  price: "CAD $399",
  period: "/month",
  description: "For new or simple businesses that need clean books and basic compliance handled.",
  features: ["Monthly bookkeeping & reconciliation", "GST/HST filing", "Management financial statements", "Cloud software setup (QBO or Xero)", "CRA correspondence support"],
  buttonText: "Get started",
  buttonVariant: "outline" as const,
  isPopular: false
}, {
  name: "GROWTH",
  price: "CAD $749",
  period: "/month",
  description: "For growing businesses with employees, higher volume, and year-end tax needs.",
  features: ["Everything in Starter", "Payroll processing (up to 10 employees)", "T4 preparation & filing", "Accounts payable & receivable", "Budget vs. actuals reporting", "Corporate tax return (T2)"],
  buttonText: "Get started",
  buttonVariant: "default" as const,
  isPopular: true
}, {
  name: "FULL-SERVICE",
  price: "CAD $1,299",
  period: "/month",
  description: "For established businesses that need strategic financial oversight and full-cycle support.",
  features: ["Everything in Growth", "Fractional CFO advisory", "Cash flow management & forecasting", "Tax planning & optimization", "KPI dashboards & lender-ready reporting", "Priority response & dedicated advisor"],
  buttonText: "Get started",
  buttonVariant: "outline" as const,
  isPopular: false
}];
export default function PricingTiers() {
  return <section id="pricing-tiers" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-sm font-semibold text-primary uppercase tracking-wider">
            Services & Pricing
          </span>
          <h2 className="text-3xl md:text-4xl font-bold font-default mt-3 mb-4 text-foreground">
            Simple, transparent pricing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            All packages are billed monthly. Pricing scales with transaction
            volume and complexity. Book a free 30-minute consultation to find
            the right fit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {pricingData.map((tier, index) => <div data-index={index} key={tier.name} className="relative flex">
              <Card className={`flex flex-col w-full h-full ${tier.isPopular ? "border-2 border-primary shadow-lg" : "border border-border shadow-sm"}`}>
                {tier.isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="default" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                      Most popular
                    </Badge>
                  </div>}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-sm font-bold text-muted-foreground tracking-widest uppercase mb-2">
                    {tier.name}
                  </CardTitle>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-4xl font-bold font-default text-foreground">
                      {tier.price}
                    </span>
                    <span className="text-muted-foreground font-medium">
                      {tier.period}
                    </span>
                  </div>
                  <CardDescription className="text-sm mt-2 h-12">
                    {tier.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 pt-4">
                  <ul className="space-y-3">
                    {tier.features.map((feature, fIndex) => <li data-index={fIndex} key={fIndex} className="flex items-start gap-3">
                        <Check className="size-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground leading-tight">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-6">
                  <Button asChild variant={tier.buttonVariant} className="w-full" size="lg">
                    <Link to="#">{tier.buttonText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}